// Company source aliases — learned "source string → company" mappings.
//
// Backed by the `company_source_aliases` table (one row per alias). Read before
// matching, upserted on approve. `kind` = 'name' (any name-identified party) or
// 'address' (load place).

import { BaseDao, type SupabaseClient } from "../base.ts";
import type { AliasKind, AliasMap } from "./awizacjaTypes.ts";

interface AliasRow {
  id: number;
  kind: AliasKind;
  key_norm: string;
  company_id: number;
  source: string | null;
}

/** Normalized alias key from a raw source name (lowercase, punctuation-folded). */
export function aliasKey(name: string): string {
  return (name ?? "").toLowerCase().replace(/[.,/–-]/g, " ").replace(/\s+/g, " ").trim();
}

/** Load-place aliases key on the departure ADDRESS (the vendor name repeats
 *  across collection sites). Must match on both the mapper and the FE side. */
export function loadPlaceAliasName(zip: string, city: string, street: string): string {
  return [zip, city, street].filter(Boolean).join(" ");
}

export interface AliasEntry {
  kind: AliasKind;
  name: string;
  companyId: number;
}

export class AwizacjaAliasStore extends BaseDao<AliasRow> {
  protected readonly dependentResources = ["company_source_aliases"];

  constructor(sb: SupabaseClient) {
    super(sb, "company_source_aliases");
  }

  async load(): Promise<AliasMap> {
    const { data, error } = await this.sb
      .from("company_source_aliases")
      .select("kind, key_norm, company_id");
    if (error) throw error;
    const map: AliasMap = { name: {}, address: {} };
    for (const row of (data ?? []) as Pick<AliasRow, "kind" | "key_norm" | "company_id">[]) {
      if (row.kind === "name" || row.kind === "address") map[row.kind][row.key_norm] = row.company_id;
    }
    return map;
  }

  /** Upsert confirmed mappings (atomic per row, unique on (kind, key_norm)). */
  async remember(entries: AliasEntry[], source = "awizacja"): Promise<void> {
    const rows = entries
      .map((e) => ({ kind: e.kind, key_norm: aliasKey(e.name), company_id: e.companyId, source }))
      .filter((r) => r.key_norm.length > 0);
    if (!rows.length) return;
    const { error } = await this.sb
      .from("company_source_aliases")
      .upsert(rows, { onConflict: "kind,key_norm" });
    if (error) throw error;
  }
}
